import UploadFile from 'components/organisms/csvUpload';
import BaseModal from 'components/atoms/base-modal';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import DownloadOutlinedIcon from 'components/atoms/icons/downloadOutlinedIcon';
import { useState, useEffect, type FC } from 'react';
import { PaperClipOutlined, DeleteOutlined } from '@ant-design/icons';
import { Alert } from 'antd';

import s from './styles.module.scss';

interface Props {
  isOpen: boolean;
  toggle: () => any;
  uploadCsvFile: (file: File) => Promise<{ error?: any }>;
  sampleFileUrl?: string;
  title: string;
  fileType?: 'csv' | 'json';
  saveButtonLabel?: string;
  width?: string | number;
}

const FileUploadModal: FC<Props> = ({
  isOpen,
  toggle,
  uploadCsvFile,
  sampleFileUrl,
  title,
  fileType = 'csv',
  saveButtonLabel,
  width = '1000px',
}) => {
  const { t } = useTranslation();
  const [localFile, setLocalFile] = useState<File>(new File([], ''));
  const [loading, setLoading] = useState<boolean>(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [uploadKey, setUploadKey] = useState(0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (localFile?.size > 0) {
      const url = URL.createObjectURL(localFile);
      setFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setFileUrl(null);
    return undefined;
  }, [localFile]);

  const clearFile = () => {
    setLocalFile(new File([], ''));
    setParseErrors([]);
    setUploadKey((prev) => prev + 1);
  };

  const uploadFile = (file: File) => {
    setLocalFile(file);
    setParseErrors([]);
  };

  return (
    <BaseModal
      open={isOpen}
      onCancel={() => {
        setLoading(false);
        clearFile();
        toggle();
      }}
      title={title}
      width={width}
      smallBodyPaddingWithFooter
      bodyClassNames={s.body}
      withFooter={[
        sampleFileUrl ? (
          <BaseButton
            dataCy="download-button"
            key="download"
            type="secondary"
            icon={<DownloadOutlinedIcon />}
            label={t('controls.downloadSample')}
            onClick={() => {
              const a = document.createElement('a');
              a.href = sampleFileUrl;
              a.download = 'importFromEHRTemplate.csv';
              a.click();
            }}
            width={173}
          />
        ) : (
          <div />
        ),
        <div style={{ width: '100%' }} />,
        <BaseButton
          dataCy="cancel-button"
          key="cancel"
          type="secondary"
          label={t('controls.cancel')}
          onClick={() => {
            setLoading(false);
            clearFile();
            toggle();
          }}
        />,
        <BaseButton
          dataCy="save-button"
          key="save"
          type="primary"
          label={saveButtonLabel ?? t('controls.sendFile')}
          loading={loading}
          onClick={async () => {
            setLoading(true);
            setParseErrors([]);
            const result = await uploadCsvFile(localFile);
            if (result?.error) {
              setParseErrors(
                Array.isArray(result.error)
                  ? result.error
                  : [result.error.toString()],
              );
              setLoading(false);
              return;
            }
            clearFile();
            setLoading(false);
          }}
          disabled={localFile?.size === 0}
        />,
      ]}
    >
      <div className="upload-csv-modal-body">
        {parseErrors.length > 0 && (
          <Alert
            message={parseErrors[0]}
            type="error"
            showIcon
            className={s['error-message']}
          />
        )}
        <UploadFile
          key={uploadKey}
          onUpload={(files) => uploadFile(files[0])}
          fileType={fileType}
          sampleFileUrl={sampleFileUrl}
          disabled={localFile?.size > 0}
        />
      </div>
      {localFile?.size > 0 && (
        <div
          className="uploaded-files-list"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="uploaded-file">
            <div className="uploaded-file-name">
              <PaperClipOutlined />
              <a
                href={fileUrl ?? '#'}
                download={localFile?.name}
                target="_blank"
                rel="noopener noreferrer"
              >
                {localFile?.name}
              </a>
            </div>
            <BaseButton
              icon={<DeleteOutlined />}
              type="secondary"
              onClick={clearFile}
              className={s['uploaded-file-remove-button']}
            />
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default FileUploadModal;
